import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GraphService } from '~/core/services/graph.service';
import { AppConfigService } from '~/core/services/appconfig.service';
import { TabNavigationService } from '~/services/tab-navigation.service';
import { ConversationHistoryService, ThreadHistorySummary } from '~/pages/home/services/conversation-history.service';

@Component({
    selector: 'nav',
    templateUrl: './nav.component.html',
    styleUrls: ['./nav.component.scss'],
    standalone: false
})
export class NavComponent implements OnInit, OnDestroy, OnChanges {
    @Input() expand: boolean = false;
    @Output() setLayoutClass = new EventEmitter();
    @Output() reloadPage = new EventEmitter();
    @Output() showSearch: EventEmitter<boolean> = new EventEmitter();
    @Output() uploadModalRequested = new EventEmitter<void>();

    public isAdmin: boolean = false;

    // simplified sidebar with chat and job history
    public activeSection: string = '';
    public showJobHistory: boolean = true;
    public selectedJobId: string = '';
    public numberOfDocuments: number = 6;
    public searchType: 'quick' | 'detailed' = 'quick';
    public selectedAgent: 'auto' | 'general' | 'ces_search' | 'ewo_qna' | 'ewo_search' | 'legacy_piping' | 'esra' | 'rims' = 'auto';
    public generateDrtTable: boolean = false;
    
    // Thread history from conversation history API
    public threadSummaries: ThreadHistorySummary[] = [];
    public isLoadingHistory: boolean = false;
    public historyLoadError: string = '';
    
    public availableAgents = [
        { id: 'auto', label: 'Auto' },
        { id: 'general', label: 'General' },
        { id: 'ces_search', label: 'CES Search' },
        { id: 'ewo_qna', label: 'EWO QnA' },
        { id: 'ewo_search', label: 'EWO Search' },
        { id: 'legacy_piping', label: 'Legacy Piping' },
        { id: 'esra', label: 'ESRA Search' },
        { id: 'rims', label: 'RIMS' }
    ];
    public jobHistoryItems: any[] = [
        { id: '6572', description: 'DRT for pump P-101' },
        { id: '6573', description: 'Blend report on compressors' },
        { id: '6574', description: 'Find thermowell repair process' }
    ];

    private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private graphService: GraphService,
        private appConfigService: AppConfigService,
        private tabNavigationService: TabNavigationService,
        private conversationHistoryService: ConversationHistoryService
    ) { }

    // INITIALIZATION - Simplified for chat and job history
    public async ngOnInit() {
        this.syncActiveSectionFromRoute();
        this.showJobHistory = true;
        this.selectedJobId = '';

        // Check admin access: verify current user is a member of the admin group
        const adminGroupId: string = this.appConfigService.getConfig('admin_obj_id') ?? '';
        if (adminGroupId) {
            this.isAdmin = await this.graphService.checkUserInGroup(adminGroupId);
        }

        const settings = this.tabNavigationService.getSearchSettings();
        this.numberOfDocuments = settings.numberOfDocuments;
        this.searchType = settings.searchType;
        this.selectedAgent = settings.selectedAgent;
        this.generateDrtTable = !!settings.generateDrtTable;

        // Fetch conversation history
        this.fetchConversationHistory();

        // Listen for new chat events to refresh history
        this.tabNavigationService.newChat$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.fetchConversationHistory();
            });

        this.router.events
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.syncActiveSectionFromRoute(event.urlAfterRedirects);
                }
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['expand'] && changes['expand'].currentValue === true) {
            this.activeSection = this.getActiveSectionForRoute(true);
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Fetch conversation history from backend
     */
    private fetchConversationHistory(): void {
        this.isLoadingHistory = true;
        this.historyLoadError = '';

        this.conversationHistoryService.fetchThreadSummaries()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    console.log('✅ [NAV] Thread summaries loaded');
                    this.threadSummaries = this.conversationHistoryService.getThreadSummaries();
                    this.isLoadingHistory = false;
                },
                error: (error) => {
                    console.error('❌ [NAV] Error fetching thread summaries:', error);
                    this.historyLoadError = 'Failed to load conversation history';
                    this.threadSummaries = [];
                    this.isLoadingHistory = false;
                }
            });
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public toggleMenu(_collapse: boolean = false) {
        // If sidebar is currently collapsed and about to open, show history by default
        if (!this.expand) {
            this.activeSection = 'job-history';
        }
        this.setLayoutClass.emit();
    }

    // Handle new chat Button Click
    public handleNewChat() {
        this.activeSection = 'new-chat';
        console.log('New chat initiated');

        this.tabNavigationService.triggerNewChat();
        this.router.navigate(['/']);
    }

    public handleUserDashboard() {
        this.activeSection = 'dashboard';
        this.router.navigate(['/dashboard']);
    }

    // Toggle job history visibility
    public setJobHistoryActive() {
        if (!this.expand) {
            // Sidebar is closed — open it and show history
            this.toggleMenu(true);
        } else {
            // Sidebar already open — toggle the history section
            this.activeSection = this.activeSection === 'job-history' ? '' : 'job-history';
        }
    }

    // Select a thread from history and navigate to it
    public selectThread(threadId: string) {
        this.selectedJobId = threadId;
        console.log('Selected thread:', threadId);
        
        // Navigate to chat with thread ID
        this.router.navigate(['/chat', threadId]);
    }

    // Select a job from history (legacy - kept for compatibility)
    public selectJob(job: any) {
        this.selectedJobId = job.id;
        console.log('Selected job:', job);
    }

    public openSearchSettings(): void {
        if (!this.expand) {
            this.toggleMenu(true);
        }
    }

    private syncActiveSectionFromRoute(url: string = this.router.url): void {
        this.activeSection = this.getActiveSectionForRoute(this.expand, url);
    }

    private getActiveSectionForRoute(expanded: boolean, url: string = this.router.url): string {
        if (url.startsWith('/dashboard')) {
            return 'dashboard';
        }

        return expanded ? 'job-history' : '';
    }

    public onDocumentsChange(value: number): void {
        this.numberOfDocuments = value;
        this.tabNavigationService.updateSearchSettings({
            numberOfDocuments: this.numberOfDocuments
        });
    }

    public onSearchTypeChange(type: 'quick' | 'detailed'): void {
        this.searchType = type;
        this.tabNavigationService.updateSearchSettings({
            searchType: this.searchType
        });
    }

    public onAgentChange(agent: string): void {
        this.selectedAgent = agent as 'auto' | 'general' | 'ces_search' | 'ewo_qna' | 'ewo_search' | 'legacy_piping' | 'esra' | 'rims';
        this.tabNavigationService.updateSearchSettings({
            selectedAgent: this.selectedAgent
        });
    }

    public onGenerateDrtTableChange(checked: boolean): void {
        this.generateDrtTable = checked;
        this.tabNavigationService.updateSearchSettings({
            generateDrtTable: this.generateDrtTable
        });
    }
}
